import posthogEvents from "@app/config/posthog/posthog-events";
import { useFavorites, useRecents } from "@app/queries/iam";
import type { Experiment, ExperimentId } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import TimeAgo from "@lib/common/components/TimeAgo/TimeAgo";
import ExperimentIcon from "@lib/experiment/ExperimentIcon/ExperimentIcon";
import ExperimentStatusBadge from "@lib/experiment/ExperimentStatusBadge/ExperimentStatusBadge";
import formatExperimentName from "@lib/experiment/utils/formatExperimentName";
import resolveExperimentLinkTo from "@lib/experiment/utils/resolveExperimentLinkTo";
import { Group, Loader, Menu, ScrollArea, Text } from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link } from "react-router";

function ExperimentMenuItem({
  experiment,
  onClick,
  selected
}: {
  experiment: Pick<Experiment, "id" | "name" | "status" | "updated_at" | "is_favorite">;
} & { onClick: VoidFunction; selected?: boolean }) {
  const { id, name, status, is_favorite, updated_at } = experiment;
  return (
    <Menu.Item
      component={Link}
      to={resolveExperimentLinkTo(id, status)}
      leftSection={<ExperimentIcon status={status} size="xs" />}
      onClick={onClick}
      bg={selected ? "var(--mantine-color-gray-light)" : undefined}
      rightSection={
        <Group gap="sm" wrap="nowrap">
          {is_favorite && <Icon name="star" size="xs" color="yellow" variant="solid" />}
          <ExperimentStatusBadge status={status} size="xs" />
          <TimeAgo date={new Date(updated_at)} size="xs" c="dimmed" lineClamp={1} lh={1} />
        </Group>
      }
    >
      <Text truncate maw="60vw">
        {formatExperimentName(name)}
      </Text>
    </Menu.Item>
  );
}

export default function ExperimentSwitcher({ children, selected }: { children: ReactNode; selected?: ExperimentId }) {
  const recentsQuery = useRecents();
  const favoritesQuery = useFavorites();
  const posthog = usePostHog();

  const onRecentExperimentClick = useCallback(
    (experimentId: ExperimentId) => {
      posthog.capture(posthogEvents.page_header_experiment_switcher.recent_experiment_click, { experimentId });
    },
    [posthog]
  );

  const onFavoriteExperimentClick = useCallback(
    (experimentId: ExperimentId) => {
      posthog.capture(posthogEvents.page_header_experiment_switcher.favorite_experiment_click, { experimentId });
    },
    [posthog]
  );

  const isLoading = useMemo(() => recentsQuery.isLoading || favoritesQuery.isLoading, [recentsQuery, favoritesQuery]);
  const isEmpty = useMemo(
    () =>
      recentsQuery.isFetched &&
      recentsQuery.data?.experiments.length === 0 &&
      favoritesQuery.isFetched &&
      favoritesQuery.data?.experiments.length === 0,
    [recentsQuery, favoritesQuery]
  );

  return (
    <Menu styles={{ item: { paddingBlock: 7 } }} shadow="xl">
      <Menu.Target>{children}</Menu.Target>
      <Menu.Dropdown maw={1100}>
        <ScrollArea.Autosize mah="70vh">
          {!isLoading && isEmpty && (
            <Menu.Item>
              <Text>Nothing here yet.</Text>
            </Menu.Item>
          )}
          {isLoading && (
            <Menu.Item leftSection={<Loader size="xs" />} style={{ whiteSpace: "nowrap" }}>
              <Text>Loading...</Text>
            </Menu.Item>
          )}
          {!isLoading && !isEmpty && (
            <>
              {recentsQuery.data?.experiments && recentsQuery.data?.experiments.length > 0 && (
                <>
                  <Menu.Label>Recent</Menu.Label>
                  {recentsQuery.data?.experiments.map(experiment => (
                    <ExperimentMenuItem
                      key={experiment.id}
                      experiment={experiment}
                      onClick={() => onRecentExperimentClick(experiment.id)}
                      selected={experiment.id === selected}
                    />
                  ))}
                  <Menu.Divider />
                </>
              )}
              {favoritesQuery.data?.experiments && favoritesQuery.data?.experiments.length > 0 && (
                <>
                  <Menu.Label>Favorites</Menu.Label>
                  {favoritesQuery.data?.experiments.map(experiment => (
                    <ExperimentMenuItem
                      key={experiment.id}
                      experiment={experiment}
                      onClick={() => onFavoriteExperimentClick(experiment.id)}
                      selected={experiment.id === selected}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}
